import { ParameterGroup } from 'communication/types/strategyParams/StrategyParametersElement';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import omitUndefined from 'utils/omitUndefined';
import { OrderFill } from './fillsGraphData';
import { FillsGraphStrategyParamData } from './store/data/FillsGraphDataState';

export interface TimeSeriesStrategyParams extends TimeSeriesChartDataItem {
    revisionIndex: number;
    limitPrice?: number;
    minParticipationRate?: number;
    maxParticipationRate?: number;
}

export function mergeStrategyParamDataAndGetFinalValues(
    sortedData: OrderFill[],
    strategyParams: FillsGraphStrategyParamData
): TimeSeriesStrategyParams | undefined {
    const stratParamSeries = computeStrategyParamsTimeseries(
        strategyParams,
        sortedData[0].time
    );

    mergeStrategyParamSeries(sortedData, stratParamSeries);

    return stratParamSeries.length > 0
        ? stratParamSeries[stratParamSeries.length - 1]
        : undefined;
}

export function computeStrategyParamsTimeseries(
    strategyParams: FillsGraphStrategyParamData,
    sessionStartTime: number
): TimeSeriesStrategyParams[] {
    const { orderSelection, paramGroups } = strategyParams;

    switch (orderSelection) {
        case 'single':
            return computeSingleOrderTimeseries(paramGroups, sessionStartTime);
        case 'multiple':
            return mapAndFilterParamGroups(paramGroups, sessionStartTime);
        default:
            return [];
    }
}

export function mergeStrategyParamSeries(
    data: OrderFill[],
    stratParamSeries: TimeSeriesStrategyParams[]
) {
    for (const sp of stratParamSeries) {
        const index = data.findIndex((d) => d.time >= sp.time);
        if (index >= 0) {
            const item = { ...data[index], ...sp };
            if (data[index].time === sp.time) {
                data[index] = item;
            } else {
                data.splice(index, 0, item);
            }
        } else {
            data.push(sp);
        }
    }

    if (stratParamSeries.length > 0 && data.length > 0) {
        data[data.length - 1] = {
            ...stratParamSeries[stratParamSeries.length - 1],
            ...data[data.length - 1],
        };
    }
}

function computeSingleOrderTimeseries(
    paramGroups: ParameterGroup[],
    sessionStartTime: number
): TimeSeriesStrategyParams[] {
    const pages: ParameterGroup[] = [];
    for (let x = 0; x < paramGroups.length; ++x) {
        const currentPage = paramGroups[x];

        // carry forward previous param values to current paramGroup
        const cumulativePage =
            x === 0 ? { ...currentPage } : { ...pages[x - 1], ...currentPage };

        pages.push(cumulativePage);
    }

    return mapAndFilterParamGroups(pages, sessionStartTime).reduce<
        TimeSeriesStrategyParams[]
    >((acc, item) => {
        /* filter duplicates - latest duplicate wins */
        if (acc.some((x) => x.time === item.time)) {
            // replace in result
            acc.splice(acc.length - 1, 1, item);
        } else {
            // add to result
            acc.push(item);
        }

        return acc;
    }, []);
}

function mapAndFilterParamGroups(
    paramGroups: ParameterGroup[],
    sessionStartTime: number
): TimeSeriesStrategyParams[] {
    return paramGroups
        .map((pg: ParameterGroup, revisionIndex: number) =>
            omitUndefined({
                time: ensureTimestampWithinTimeseries(
                    pg['SENDING_TIME'],
                    sessionStartTime
                )!, // Assume that we always get SENDING_TIME,
                revisionIndex,
                limitPrice: getStratParamValue(pg['LIMIT_PRICE']),
                minParticipationRate: getStratParamValue(
                    pg['MIN_PARTICIPATION_RATE']
                ),
                maxParticipationRate: getStratParamValue(
                    pg['MAX_PARTICIPATION_RATE']
                ),
            })
        )
        .filter((x) => x.time !== undefined && Object.keys(x).length > 2); // ensure that groups have at least SENDING_TIME and revisionIndex, along with other values
}

function ensureTimestampWithinTimeseries(
    timestamp: number | null | undefined,
    sessionStartTime: number
): number | undefined {
    return timestamp !== undefined
        ? timestamp === null || timestamp < sessionStartTime
            ? sessionStartTime
            : timestamp
        : undefined;
}

function getStratParamValue(
    value: number | null | undefined
): number | undefined {
    return value ? value : undefined;
}
